import { useEffect, useMemo, useRef, useState } from 'react';
import { IconSearch, IconX } from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { SYMBOL_GROUPS, searchSymbols } from '../engine/symbolCatalogue';

/**
 * Special characters — currency, maths, arrows, punctuation, accented Latin and
 * Greek — in the same shape as the emoji picker.
 *
 * Unlike an emoji these go in as TEXT, because that is what they are: a section
 * sign or a Greek letter belongs in a sentence, where it can be retyped,
 * restyled and searched. The trade is that a glyph the chosen font does not
 * carry will fall back at export; the preview here is drawn in the same face
 * the page uses, so anything that will not survive looks wrong before you
 * place it.
 */
export default function SymbolModal({ fontCss, onClose, onApply }) {
    const { t } = useTranslation();
    const [query, setQuery] = useState('');
    const [group, setGroup] = useState(SYMBOL_GROUPS[0].id);
    const searchRef = useRef(null);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
        window.addEventListener('keydown', onKey, true);
        requestAnimationFrame(() => searchRef.current?.focus());
        return () => window.removeEventListener('keydown', onKey, true);
    }, [onClose]);

    const shown = useMemo(() => {
        const q = query.trim();
        if (q) return searchSymbols(q);
        return SYMBOL_GROUPS.find((g) => g.id === group)?.chars ?? [];
    }, [query, group]);

    return (
        <div
            className="fixed inset-0 z-[9200] flex items-center justify-center bg-[#1C1815]/45 p-4"
            role="presentation"
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label={t('pdfEditor.symbols.title')}
                onClick={(e) => e.stopPropagation()}
                className="flex max-h-[min(560px,90vh)] w-full max-w-[420px] flex-col overflow-hidden rounded-2xl border border-[#ECE8E4] bg-white shadow-[0_20px_50px_rgba(28,24,21,0.25)] dark:border-zinc-700 dark:bg-zinc-900"
            >
                <div className="flex items-center gap-2 border-b border-[#ECE8E4] px-3 py-2.5 dark:border-zinc-700">
                    <div className="relative flex-1">
                        <IconSearch size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A8A099] dark:text-zinc-500" />
                        <input
                            ref={searchRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t('pdfEditor.symbols.search')}
                            className="h-9 w-full rounded-lg border border-[#ECE8E4] bg-white pl-8 pr-2 text-[13px] text-[#1C1815] outline-none transition-colors placeholder:text-[#A8A099] focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('common.close')}
                        className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-[#A8A099] transition-colors hover:bg-[#F7F5F3] hover:text-[#3D3833] dark:hover:bg-zinc-800"
                    >
                        <IconX size={16} stroke={2} />
                    </button>
                </div>

                {!query.trim() ? (
                    <div className="flex gap-0.5 border-b border-[#ECE8E4] px-2 py-1.5 dark:border-zinc-700">
                        {SYMBOL_GROUPS.map((g) => (
                            <button
                                key={g.id}
                                type="button"
                                onClick={() => setGroup(g.id)}
                                title={t(`pdfEditor.symbols.groups.${g.id}`)}
                                aria-label={t(`pdfEditor.symbols.groups.${g.id}`)}
                                aria-pressed={group === g.id}
                                className={`flex h-8 flex-1 items-center justify-center rounded-md text-[15px] leading-none transition-colors ${
                                    group === g.id ? 'bg-[#F7F5F3] dark:bg-zinc-800' : 'opacity-55 hover:opacity-100'
                                }`}
                            >
                                {g.icon}
                            </button>
                        ))}
                    </div>
                ) : null}

                <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
                    {!query.trim() ? (
                        <p className="px-1 pb-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-[#A8A099] dark:text-zinc-500">
                            {t(`pdfEditor.symbols.groups.${group}`)}
                        </p>
                    ) : null}
                    {shown.length ? (
                        <div className="grid grid-cols-8 gap-0.5">
                            {shown.map((item) => (
                                <button
                                    key={item.char}
                                    type="button"
                                    onClick={() => { onApply(item.char); onClose(); }}
                                    title={item.name}
                                    aria-label={item.name}
                                    // Drawn in the page's own face, so a glyph it
                                    // cannot render looks wrong here first.
                                    style={{ fontFamily: fontCss }}
                                    className="flex aspect-square items-center justify-center rounded-md text-[19px] leading-none text-[#1C1815] transition-colors hover:bg-[#F7F5F3] dark:text-zinc-100 dark:hover:bg-zinc-800"
                                >
                                    {item.char}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <p className="px-1 py-8 text-center text-[12.5px] text-[#A8A099] dark:text-zinc-500">
                            {t('pdfEditor.symbols.noMatch')}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
